<template>
  <v-container class="pa-0">
    <Loader size="small" text="Gathering Locations" v-if="loading"></Loader>
    <div v-if="!loading">
      <v-select
        :items="locationTypeOptions"
        item-text="name"
        item-value="id"
        v-model="selectedTab"
      >
      </v-select>

      <v-row v-if="selectedTab === 'geography'" dense row wrap justify="center">
        <v-col class="col-12">
          <div
            class="text-center places-count-text"
            :class="{ 'red--text': maxAoiIdSelected }"
          >
            {{ aoiIdSelectedCount }} out of {{ maxAoiIdAllowed }} selected
          </div>
        </v-col>
      </v-row>

      <v-row
        v-show="selectedTab === 'locations'"
        dense
        row
        wrap
        justify="center"
      >
        <v-col class="col-12 mt-2 d-flex justify-center align-center">
          <v-btn-toggle
            mandatory
            v-model="selectedTabLocations"
            dense
            class="btn-toggle"
          >
            <v-btn v-show="assets && assets.length" x-small value="assets"
              >My Assets</v-btn
            >
            <v-btn v-show="places && places.length" x-small value="places"
              >My Places</v-btn
            >
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row dense row wrap justify="center">
        <v-col class="col-12">
          <div v-if="selectedTab === 'geography' || !places || !places.length">
            <div class="pt-0 pb-1">
              <v-autocomplete
                v-model="regionsSelected"
                :search-input.sync="inputTextRegions"
                :items="availableRegions"
                chips
                small-chips
                deletable-chips
                hide-details
                label="Regions"
                multiple
                clearable
                return-object
                item-text="name"
                item-value="id"
                class="autocomplete"
                @change="handleAoiChange('regions')"
              ></v-autocomplete>
            </div>
            <div class="py-1">
              <v-autocomplete
                v-model="countriesSelected"
                :search-input.sync="inputTextCountries"
                :items="availableCountries"
                chips
                small-chips
                deletable-chips
                hide-details
                label="Countries"
                multiple
                clearable
                return-object
                item-text="name"
                item-value="id"
                class="autocomplete"
                @change="handleAoiChange('countries')"
              ></v-autocomplete>
            </div>
            <div class="py-1">
              <v-autocomplete
                v-model="countiesSelected"
                :search-input.sync="inputTextCounties"
                :items="availableCounties"
                chips
                small-chips
                deletable-chips
                hide-details
                label="US Counties"
                multiple
                clearable
                return-object
                item-text="name"
                item-value="id"
                class="autocomplete"
                @change="handleAoiChange('counties')"
              ></v-autocomplete>
            </div>
            <div class="py-1">
              <v-autocomplete
                v-model="citiesSelected"
                :search-input.sync="inputTextCities"
                :items="availableCities"
                chips
                small-chips
                deletable-chips
                hide-details
                label="Cities"
                multiple
                clearable
                return-object
                item-text="name"
                item-value="id"
                class="autocomplete"
                @change="handleAoiChange('cities')"
              ></v-autocomplete>
            </div>
            <div class="py-1" v-if="hasMaritimeAccess">
              <v-autocomplete
                v-model="watersSelected"
                :search-input.sync="inputTextWaters"
                :items="availableWaters"
                chips
                small-chips
                deletable-chips
                hide-details
                label="Waters"
                multiple
                clearable
                return-object
                item-text="name"
                item-value="id"
                class="autocomplete"
                @change="handleAoiChange('waters')"
              ></v-autocomplete>
            </div>
          </div>

          <div
            v-if="
              selectedTab === 'locations' && selectedTabLocations === 'assets'
            "
          >
            <v-container class="pa-0">
              <v-row dense row wrap justify="center">
                <v-col class="col-12">
                  <MonitorRadiusField
                    :monitorRadius="radius"
                    :poisDistUnits="this.preferences.poisDistUnits"
                    @update-radius="handleMonitorRadiusChange"
                  ></MonitorRadiusField>
                </v-col>
              </v-row>

              <v-row dense row wrap justify="center">
                <v-col class="col-12">
                  <div
                    class="text-center places-count-text"
                    :class="{ 'red--text': maxAssetsSelected }"
                  >
                    {{ assetsSelectedCount }} out of
                    {{ maxAssetsAllowed }} selected
                  </div>
                </v-col>
              </v-row>

              <v-row dense row wrap>
                <v-col class="col-12">
                  <SearchFilterAssets
                    v-show="
                      selectedTab === 'locations' ||
                      selectedTabLocations === 'assets'
                    "
                    :max-allowed="maxAssetsAllowed"
                    :incoming="assetsSelectedIds"
                    @change="handleAssetChange"
                  ></SearchFilterAssets>
                </v-col>
              </v-row>
            </v-container>
          </div>

          <div
            v-show="
              selectedTab === 'places' ||
              (selectedTab === 'locations' && selectedTabLocations === 'places')
            "
          >
            <v-container class="pa-0">
              <v-row dense row wrap justify="center">
                <v-col class="col-12">
                  <MonitorRadiusField
                    :monitorRadius="radius"
                    :poisDistUnits="this.preferences.poisDistUnits"
                    @update-radius="handleMonitorRadiusChange"
                  ></MonitorRadiusField>
                </v-col>
              </v-row>

              <v-row dense row wrap justify="center">
                <v-col class="col-12">
                  <div
                    class="text-center places-count-text"
                    :class="{ 'red--text': maxPlacesSelected }"
                  >
                    {{ placesSelectedCount }} out of
                    {{ maxPlacesAllowed }} selected
                  </div>
                </v-col>
              </v-row>

              <v-row dense row wrap>
                <v-col class="col-12">
                  <v-expansion-panels accordion class="places-expander">
                    <v-expansion-panel
                      v-for="place in places"
                      :key="place.uniqueId"
                    >
                      <v-expansion-panel-header
                        v-if="!place.isDefaultLayer"
                        expand-icon="mdi-menu-down"
                        :title="place.name"
                      >
                        <img
                          :src="getImgSrc(place)"
                          class="pull-left"
                          :width="12"
                          alt="place icon"
                        />
                        <div
                          class="ml-4 places-name-label application-heading pull-left"
                        >
                          {{ place.name }}
                        </div>
                        <div class="pull-right places-count-label mr-1">
                          {{ getPlaceNumSelected(place) }}
                        </div>
                        <div class="clearfix"></div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content v-if="!place.isDefaultLayer">
                        <div
                          v-for="(item, index) in place.data.features"
                          :key="index"
                          class="places-item-names ml-1 pb-1"
                        >
                          <label
                            class="places-item-label"
                            :title="getDisplayValue(item, place)"
                          >
                            <input
                              type="checkbox"
                              name="checkbox"
                              value="text"
                              class="places-item-checkbox"
                              :disabled="isItemDisabled(item)"
                              :checked="getItemChecked(item)"
                              @change="handlePlaceChange(item)"
                            />
                            <span>{{ getDisplayValue(item, place) }}</span>
                          </label>
                        </div>
                        <div
                          v-if="
                            !place.data.features || !place.data.features.length
                          "
                          class="places-item-names"
                        >
                          No places found
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-container>
          </div>
          <div v-if="selectedTab === 'polygon'">
            <SearchPolygonEditor
              :currenttab="selectedTab"
              :polygon="incomingPolygon"
              :is-open="searchPolygonEditorOpen"
              @apply="applyPolygon"
              @clear="clearPolygon"
              @editing="updateEditing"
            ></SearchPolygonEditor>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";
import lodashFind from "lodash/find";
import lodashUniq from "lodash/uniq";
import lodashIsEqual from "lodash/isEqual";

import { mapGetters } from "vuex";

import { GET_PLACES } from "@/store/actions.type";
import {
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_ASSETS,
} from "@/store";
import { ICONS } from "@/common/constants/icons";
import { PLACES_IMAGE_URL } from "@/common/constants/api";
import {
  SEARCH_LOCATION_RADIUS_DEFAULT,
  SEARCH_LOCATION_MAX_ALLOWED,
  SEARCH_AOIID_MAX_ALLOWED,
} from "@/common/constants/savedSearch";
import {
  getAllRegions,
  getAllCountries,
  getAllCounties,
  getAllCities,
  getWaters,
} from "@/common/maps/utilities";
import { hasMaritimeAccess } from "@/services/user/user.service";
import { getAssets } from "@/services/assetManagement/assets.service";

const EMITTED_ACTIONS = {
  CHANGE: "change",
  EDITING_POLYGON: "editing-polygon",
};

export default {
  name: "SearchFilterLocations",
  props: {
    incomingBoundsType: {
      type: String,
    },
    incomingAoiId: {
      type: Array,
    },
    incomingPlaces: {
      type: Array,
    },
    incomingAssets: {
      type: Array,
    },
    incomingPolygon: {
      type: String,
    },
    reset: {
      type: String,
    },
    selectedUnitsRadius: {
      type: Object,
      required: false, //TODO FIX THIS HOT TRASH
    },
    selectorPanelIsOpen: {
      type: Boolean,
      default: false,
    },
    locationFacetIsOpen: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Loader: () => import("@/components/common/Loader"),
    SearchFilterAssets: () => import("@/components/search/SearchFilterAssets"),
    SearchPolygonEditor: () => import("@/components/map/SearchPolygonEditor"),
    MonitorRadiusField: () => import("@/components/common/MonitorRadiusField"),
  },
  data() {
    return {
      selectedTab: "geography",
      selectedTabLocations: "assets",
      radius: SEARCH_LOCATION_RADIUS_DEFAULT,
      enabled: [],
      maxAssetsAllowed: SEARCH_LOCATION_MAX_ALLOWED,
      maxPlacesAllowed: SEARCH_LOCATION_MAX_ALLOWED,
      maxAoiIdAllowed: SEARCH_AOIID_MAX_ALLOWED,
      aoiIdSelected: [],
      placesSelected: [],
      assetsSelected: [],
      assetsSelectedIds: null,
      poisDistUnits: "mi",
      polygon: null,
      regionsSelected: [],
      availableRegions: [],
      countriesSelected: [],
      availableCountries: [],
      countiesSelected: [],
      availableCounties: [],
      citiesSelected: [],
      availableCities: [],
      watersSelected: [],
      availableWaters: [],
      typeChanged: null,
      transformedRadius: null,
      loading: false,
      inputTextRegions: "",
      inputTextCountries: "",
      inputTextCities: "",
      inputTextCounties: "",
      inputTextWaters: "",
      hasMaritimeAccess: hasMaritimeAccess(),
      assets: null,
      locationTypeOptions: [
        { id: "worldwide", name: "All Data" },
        { id: "geography", name: "Geographies" },
        { id: "mapextent", name: "Map Extent" },
        { id: "polygon", name: "Polygon" },
        //TODO assets oughta be broken out!!!
        {
          id: "locations",
          name: "Assets | Places",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      places: MODULE_PREFIX_PLACES + "places",
      preferences: MODULE_PREFIX_USER + "preferences",
      assetTypes: MODULE_PREFIX_ASSETS + "assetTypes",
    }),
    assetsSelectedCount() {
      return this.assetsSelected && this.assetsSelected.length
        ? this.assetsSelected.length
        : 0;
    },
    placesSelectedCount() {
      return this.placesSelected && this.placesSelected.length
        ? this.placesSelected.length
        : 0;
    },
    aoiIdSelectedCount() {
      return this.aoiIdSelected && this.aoiIdSelected.length
        ? this.aoiIdSelected.length
        : 0;
    },
    maxAssetsSelected() {
      return this.maxAssetsAllowed <= this.assetsSelectedCount;
    },
    maxPlacesSelected() {
      return this.maxPlacesAllowed <= this.placesSelectedCount;
    },
    maxAoiIdSelected() {
      return this.maxAoiIdAllowed <= this.aoiIdSelectedCount;
    },
    prefDistanceUnits() {
      const unit = this.preferences?.poisDistUnits;
      return unit ? (unit === "miles" ? "mi" : "km") : "mi";
    },
    searchPolygonEditorOpen() {
      return this.selectorPanelIsOpen && this.locationFacetIsOpen;
    },
  },
  async mounted() {
    this.loading = true;

    // purposely not using await for calling setUpPlaces so that this mounted
    // function doesn't sit and wait since getting places can take some time.
    this.setUpPlaces();
    this.setUpAssets();

    await Promise.all([
      this.setUpRegions(),
      this.setUpCountries(),
      this.setUpCounties(),
      this.setUpCities(),
      this.setUpWaters(),
    ]);

    this.loading = false;
    this.getPrefs();
    this.handleChangeAoiIds(this.aoiIdSelected, null);
  },
  methods: {
    async setUpRegions() {
      const items = await getAllRegions();
      let regions = [];
      if (items) {
        const len = items.length;
        for (let i = 0; i < len; i++) {
          const region = items[i];
          if (
            lodashGet(region, "properties.code") &&
            lodashGet(region, "properties.name")
          ) {
            regions.push({
              id: region.properties.code,
              name: region.properties.name,
            });
          }
        }
        regions = lodashOrderBy(regions, ["name"], ["asc"]);
      }
      this.availableRegions = regions;
    },
    async setUpCountries() {
      const items = await getAllCountries();
      let countries = [];
      if (items) {
        const len = items.length;
        for (let i = 0; i < len; i++) {
          const country = items[i];
          if (
            lodashGet(country, "properties.iso2") &&
            lodashGet(country, "properties.displayName")
          ) {
            countries.push({
              id: country.properties.iso2,
              name: country.properties.displayName,
            });
          }
        }
        countries = lodashOrderBy(countries, ["name"], ["asc"]);
      }
      this.availableCountries = countries;
    },
    async setUpCounties() {
      const items = await getAllCounties();
      let counties = [];
      if (items) {
        const len = items.length;
        for (let i = 0; i < len; i++) {
          const county = items[i];
          if (
            lodashGet(county, "properties.code") &&
            lodashGet(county, "properties.displayName") &&
            lodashGet(county, "properties.stateAbbrev")
          ) {
            counties.push({
              id: county.properties.code,
              name: `${
                county.properties.displayName
              } County (${county.properties.stateAbbrev.toUpperCase()})`,
            });
          }
        }
        counties = lodashOrderBy(counties, ["name"], ["asc"]);
      }
      this.availableCounties = counties;
    },
    async setUpCities() {
      const items = await getAllCities();
      let cities = [];
      if (items) {
        const len = items.length;
        for (let i = 0; i < len; i++) {
          const city = items[i];
          if (
            lodashGet(city, "properties.code") &&
            lodashGet(city, "properties.displayName") &&
            lodashGet(city, "properties.country")
          ) {
            cities.push({
              id: city.properties.code,
              name: `${
                city.properties.displayName
              } (${city.properties.country.toUpperCase()})`,
            });
          }
        }
        cities = lodashOrderBy(cities, ["name"], ["asc"]);
      }
      this.availableCities = cities;
    },
    async setUpWaters() {
      if (this.hasMaritimeAccess) {
        const items = await getWaters();
        let waters = [];
        if (items) {
          const len = items.length;
          for (let i = 0; i < len; i++) {
            const water = items[i];
            if (lodashGet(water, "code") && lodashGet(water, "name[0].text")) {
              waters.push({
                id: water.code,
                name: water.name[0].text,
              });
            }
          }
          waters = lodashOrderBy(waters, ["name"], ["asc"]);
        }
        this.availableWaters = waters;
      }
    },
    async setUpPlaces() {
      this.placesSelected = this.incomingPlaces || [];
      if (!this.places || !this.places.length) {
        await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
      }
    },
    async setUpAssets() {
      this.assetsSelected = this.incomingAssets || [];
      if (!this.assets) {
        const results = await getAssets({
          options: { fields: ["id"], itemsPerPage: 10 },
        });
        this.assets = results?.assets ? results.assets : null;
      }
    },
    async getPrefs() {
      if (lodashGet(this.preferences, "poisDistUnits")) {
        let unit = lodashGet(this.preferences, "poisDistUnits");
        this.poisDistUnits = unit === "miles" ? "mi" : "km";
      } else {
        this.poisDistUnits = "mi";
      }
    },
    getImgSrc(place) {
      if (!place) {
        return null;
      }

      let imgSrcActive;
      let imgSrcInactive;
      if (
        lodashGet(place, "icons") &&
        place.icons.toLowerCase() !== "default" &&
        lodashGet(place, "group")
      ) {
        const URL = PLACES_IMAGE_URL;
        imgSrcActive = `${URL}/${place.group}/${place.icons}.png`;
        imgSrcInactive = `${URL}/${place.group}/${place.icons}.png`;
      } else {
        const ICON_ACTIVE = ICONS.places["default"].active;
        const ICON_INACTIVE = ICONS.places["default"].inactive;
        imgSrcActive = require(`@/assets/images${ICON_ACTIVE}`);
        imgSrcInactive = require(`@/assets/images${ICON_INACTIVE}`);
      }

      return place.enabled ? imgSrcInactive : imgSrcActive;
    },
    getDisplayValue(item, place) {
      if (
        lodashGet(place, "displayFields") &&
        Array.isArray(place.displayFields) &&
        place.displayFields.length
      ) {
        const values = [];
        place.displayFields.forEach((field) => {
          if (field && lodashGet(item, `properties[${field}]`)) {
            values.push(item.properties[field]);
          }
        });
        return values.join(" - ");
      }
      return null;
    },
    getPlaceNumSelected(place) {
      if (!this.placesSelected) {
        return 0;
      }

      let counter = 0;
      place?.data?.features?.forEach((item) => {
        if (item?.id && this.placesSelected.includes(item.id)) {
          counter++;
        }
      });
      return counter;
    },
    getItemChecked(item) {
      if (item && item.id) {
        return this.placesSelected.includes(item.id) ? "checked" : "";
      }
      return "";
    },
    isItemDisabled(item) {
      return this.maxPlacesSelected && !this.placesSelected.includes(item.id);
    },
    handleMonitorRadiusChange(value) {
      this.radius = value;
      this.transformedRadius = this.formatRadius(this.radius);
      if (this.placesSelected || this.assetsSelected) {
        this.emitChange();
      }
    },
    handleChangeBounds() {
      this.emitChange();
    },
    handleAoiChange(typeChanged) {
      this.typeChanged = typeChanged;
      let aoiId = [];
      aoiId = aoiId.concat(this.regionsSelected);
      aoiId = aoiId.concat(this.countriesSelected);
      aoiId = aoiId.concat(this.citiesSelected);
      aoiId = aoiId.concat(this.countiesSelected);
      aoiId = aoiId.concat(this.watersSelected);
      aoiId = aoiId.map((aoi) => {
        return aoi.id;
      });
      this.aoiIdSelected = lodashUniq(aoiId);
      this.emitChange();
    },
    handleAssetChange(items) {
      this.placesSelected = [];
      this.assetsSelected = items;
      this.emitChange();
    },
    handlePlaceChange(item) {
      this.assetsSelected = [];
      if (!this.placesSelected) {
        this.placesSelected = [];
      }
      if (this.placesSelected.includes(item.id)) {
        const index = this.placesSelected.indexOf(item.id);
        if (index > -1) {
          this.placesSelected.splice(index, 1);
        }
      } else {
        this.placesSelected.push(item.id);
      }
      this.emitChange();
    },
    emitChange: lodashDebounce(function () {
      let boundsType = this.selectedTab;
      if (this.selectedTab === "locations") {
        boundsType = this.selectedTabLocations;
      } else if (this.selectedTab === "polygon") {
        boundsType = "polygon";
      }
      let toEmit = { boundsType };
      if (this.selectedTab === "polygon") {
        toEmit.polygon = this.polygon;
      }
      if (this.selectedTab === "geography") {
        toEmit.aoiId = this.aoiIdSelected;
      } else if (
        this.selectedTab === "locations" &&
        this.selectedTabLocations === "assets"
      ) {
        toEmit.radius = this.transformedRadius;
        toEmit.poisDistUnits = this.poisDistUnits || this.prefDistanceUnits;
        toEmit.assets = this.assetsSelected;
      } else if (
        this.selectedTab === "places" ||
        this.selectedTabLocations === "places"
      ) {
        toEmit.radius = this.transformedRadius;
        toEmit.poisDistUnits = this.poisDistUnits || this.prefDistanceUnits;
        toEmit.places = this.placesSelected;
      }
      this.$emit(EMITTED_ACTIONS.CHANGE, toEmit);
    }, 800),
    handleChangeAoiIds(newVal, oldVal) {
      if (!lodashIsEqual(newVal, oldVal)) {
        const regions = [];
        const countries = [];
        const cities = [];
        const counties = [];
        const waters = [];

        if (this.incomingAoiId) {
          this.incomingAoiId.forEach((aoi) => {
            let found = lodashFind(this.availableRegions, ["id", aoi]);
            if (found) {
              regions.push(found);
            } else {
              found = lodashFind(this.availableCountries, ["id", aoi]);
              if (found) {
                countries.push(found);
              } else {
                found = lodashFind(this.availableCities, ["id", aoi]);
                if (found) {
                  cities.push(found);
                } else {
                  found = lodashFind(this.availableCounties, ["id", aoi]);
                  if (found) {
                    counties.push(found);
                  } else {
                    found = lodashFind(this.availableWaters, ["id", aoi]);
                    if (found) {
                      waters.push(found);
                    }
                  }
                }
              }
            }
          });
        }

        this.regionsSelected = regions;
        this.countriesSelected = countries;
        this.citiesSelected = cities;
        this.countiesSelected = counties;
        this.watersSelected = waters;

        let aoiId = [];
        aoiId = aoiId.concat(this.regionsSelected);
        aoiId = aoiId.concat(this.countriesSelected);
        aoiId = aoiId.concat(this.citiesSelected);
        aoiId = aoiId.concat(this.countiesSelected);
        aoiId = aoiId.concat(this.watersSelected);
        aoiId = aoiId.map((aoi) => {
          return aoi.id;
        });
        this.aoiIdSelected = lodashUniq(aoiId);

        // this bit of code is used to remove the last item added
        // if it is more than the allowed number of aois selected
        if (
          this.aoiIdSelected &&
          this.aoiIdSelected.length > this.maxAoiIdAllowed
        ) {
          if (this.typeChanged === "regions") {
            this.regionsSelected.splice(-1);
          }
          if (this.typeChanged === "countries") {
            this.countriesSelected.splice(-1);
          }
          if (this.typeChanged === "cities") {
            this.citiesSelected.splice(-1);
          }
          if (this.typeChanged === "counties") {
            this.countiesSelected.splice(-1);
          }
          if (this.typeChanged === "waters") {
            this.watersSelected.splice(-1);
          }
          this.handleAoiChange();
        }
      }
    },
    formatRadius(radiusKm) {
      if (radiusKm === null || radiusKm === undefined) {
        return;
      }

      if (this.preferences?.poisDistUnits === "miles") {
        radiusKm = radiusKm * 0.621371192237334 + 0.001; // Correct for slight imprecision with conversion;
      }
      return radiusKm;
    },
    applyPolygon(polygon) {
      if (polygon === null && !this.polygon) {
        this.selectedTab = "mapextent";
      }
      this.polygon = polygon;
      this.emitChange();
    },
    clearPolygon() {
      this.emitChange();
    },
    updateEditing(isEditing) {
      if (!isEditing && !this.polygon) {
        this.selectedTab = "mapextent";
      }
      this.$emit(EMITTED_ACTIONS.EDITING_POLYGON, isEditing);
    },
  },
  watch: {
    selectedTab: {
      handler() {
        //Don't change state when entering polygon tab as navigating away from it without applying should revert
        if (this.selectedTab !== "polygon") {
          this.emitChange();
        }
      },
    },
    regionsSelected: {
      handler() {
        this.inputTextRegions = "";
      },
    },
    countriesSelected: {
      handler() {
        this.inputTextCountries = "";
      },
    },
    citiesSelected: {
      handler() {
        this.inputTextCities = "";
      },
    },
    countiesSelected: {
      handler() {
        this.inputTextCounties = "";
      },
    },
    watersSelected: {
      handler() {
        this.inputTextWaters = "";
      },
    },
    incomingBoundsType: {
      handler() {
        if (this.incomingBoundsType === "assets") {
          this.selectedTab = "locations";
          this.selectedTabLocations = "assets";
        } else if (this.incomingBoundsType === "places") {
          this.selectedTab = "locations";
          this.selectedTabLocations = "places";
        } else {
          this.selectedTab = this.incomingBoundsType;
        }
      },
      immediate: true,
    },
    incomingAoiId: {
      handler(newVal, oldVal) {
        this.handleChangeAoiIds(newVal, oldVal);
      },
      deep: true,
    },
    incomingAssets: {
      handler(newVal, oldVal) {
        if (!lodashIsEqual(newVal, oldVal)) {
          if (this.incomingAssets && this.incomingAssets.length) {
            this.assetsSelected = lodashCloneDeep(this.incomingAssets);
            this.selectedTab = "locations";
            this.selectedTabLocations = "assets";
          } else {
            this.assetsSelected = [];
          }
        }
      },
      deep: true,
    },
    assetsSelected: {
      handler(newVal, oldVal) {
        if (!lodashIsEqual(newVal, oldVal)) {
          this.assetsSelectedIds =
            this.assetsSelected && this.assetsSelected.length
              ? this.assetsSelected.map((asset) => {
                  return typeof asset === "object" && asset !== null
                    ? asset.id
                    : asset;
                })
              : null;
        }
      },
      deep: true,
    },
    incomingPlaces: {
      handler(newVal, oldVal) {
        if (!lodashIsEqual(newVal, oldVal)) {
          if (this.incomingPlaces && this.incomingPlaces.length) {
            this.placesSelected = lodashCloneDeep(this.incomingPlaces);
            this.selectedTab = "locations";
            this.selectedTabLocations = "places";
          } else {
            this.placesSelected = [];
          }
        }
      },
      deep: true,
    },
    selectedUnitsRadius: {
      handler(newVal, oldVal) {
        this.$nextTick(() => {
          const currentVal = this.selectedUnitsRadius;
          const decimalRadius = Math.round(this.radius * 100) / 100;
          if (currentVal) {
            const decimalUnitRadius = Math.round(currentVal.radius * 100) / 100;

            if (decimalRadius !== decimalUnitRadius) {
              this.radius =
                this.selectedUnitsRadius.radius ||
                SEARCH_LOCATION_RADIUS_DEFAULT;
            }

            this.transformedRadius =
              this.formatRadius(this.selectedUnitsRadius.radius) ||
              SEARCH_LOCATION_RADIUS_DEFAULT;

            this.poisDistUnits = this.prefDistanceUnits;

            if (this.placesSelected.length) {
              const names = [];
              const placeUID = {};
              this.enabled = [];

              this.places.forEach((place) => {
                placeUID[place.data.features[0].id.split("-")[0]] =
                  place.uniqueId;
              });
              this.placesSelected.forEach((place) => {
                const name = place.split("-")[0];
                if (!names.includes(name)) {
                  names.push(name);
                  this.enabled.push(placeUID[name]);
                }
              });
            }
          }
        });
      },
      immediate: true,
      deep: true,
    },
    reset: {
      handler() {
        this.selectedTab = "geography";
        this.selectedTabLocations = "assets";
        this.poisDistUnits = this.prefDistanceUnits;
        this.radius = SEARCH_LOCATION_RADIUS_DEFAULT;
        this.transformedRadius = SEARCH_LOCATION_RADIUS_DEFAULT;
        this.aoiIdSelected = [];
        this.regionsSelected = [];
        this.countriesSelected = [];
        this.citiesSelected = [];
        this.countiesSelected = [];
        this.watersSelected = [];
        this.assetsSelected = [];
        this.placesSelected = [];
        this.emitChange();
      },
    },
  },
  beforeDestroy() {
    this.aoiIdSelected = [];
    this.regionsSelected = [];
    this.countriesSelected = [];
    this.citiesSelected = [];
    this.countiesSelected = [];
    this.watersSelected = [];
    this.placesSelected = [];
    this.assetsSelected = [];
  },
};
</script>

<style>
.places-slider .v-input__slot {
  margin-bottom: 0;
}
.places-slider .v-messages {
  display: none;
}
.places-expander .v-expansion-panel-header {
  padding: 8px 16px !important;
}
.autocomplete .v-label {
  font-size: 12px !important;
}
.bounds-radio {
  margin-top: 0;
  padding-top: 0;
}
.btn-toggle .v-btn.v-size--x-small {
  font-size: 0.5rem !important;
}
</style>

<style scoped>
.places-slider-text {
  font-size: 14px;
}
.places-count-text {
  font-size: 12px;
}
.places-item {
  cursor: pointer;
}
.places-selector-row {
  height: 40px;
}
.places-item-row {
  max-height: 250px;
  overflow-y: auto;
}
.places-name-label {
  color: #1c1c1c;
  font-size: 12px !important;
  margin: 0 0 0 5px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 160px;
}
.places-item-names {
  font-size: 12px;
  max-width: 210px;
}
.places-item-label {
  display: flex;
  align-items: baseline;
  cursor: pointer;
  overflow-x: hidden;
  white-space: nowrap;
}
.places-count-label {
  width: 30px;
  text-align: right;
  font-size: 11px;
}
.places-item-checkbox-container {
  position: relative;
  top: -8px;
}
.places-item-checkbox {
  margin-right: 8px;
  position: relative;
  top: 2px;
  cursor: pointer;
}
.places-count-total {
  font-size: 12px;
  position: relative;
  top: -1px;
  padding-left: 5px;
}
</style>
